/**
 * ONE BONE — free underwear for new customers (cart side).
 *
 * A Shopify function cannot add a line to a cart, so this is what puts the gift
 * there. The function (`gift-app/extensions/new-customer-gift`) is what
 * makes it free, and it re-checks everything server-side: the shopper must be
 * signed in with no previous orders, the variant must be one the merchant named
 * in gwp.gift_variants, and the line must carry the `_gwp` property written
 * below.
 *
 * That property is the only reason a deliberately-bought pair of the same boxer
 * brief is not silently zeroed, so it has to be written on every add.
 *
 * The variant list is why nothing here filters variants: the buttons this drives
 * are rendered from it in Liquid, so whatever reaches `select()` is already a
 * variant the function will discount. Adding a second opinion in JavaScript
 * would only give the two a way to differ — and it would be the weaker of the
 * two, since anyone can call cart/add.js directly. The cart line notice is what
 * catches a gift line that arrived some other way.
 *
 * Sizing comes from the theme's one fit finder (window.OneBoneFit). A stored
 * fit arrives marked and chosen, so the gift is one click away. A shopper
 * without one is sent to the picker, which comes back on "Lock in My Size":
 * the recommendation lands on a size button and the shopper is handed back to
 * the cart they came from (OB-257).
 */

(() => {
  /** The property the discount function looks for. Changing it breaks the gift. */
  const GIFT_PROPERTY = '_gwp';
  const GIFT_PROPERTY_VALUE = 'new-customer-underwear';

  /**
   * "Not now", remembered.
   *
   * Session storage rather than local: a shopper who waves the offer away
   * should not be asked again while they browse, but a free pair is real money
   * and withholding it forever on one dismissal would be the theme deciding
   * something the shopper did not. The next visit offers it again.
   *
   * The flag is a class on <html>, not state inside the block. The cart
   * re-renders this markup from the server after every change, so component
   * state would be thrown away on the next add — and the cart page carries the
   * offer twice, in the page and in the drawer, which a document-level class
   * keeps in step for free. assets/gwp-gift.css does the hiding.
   */
  const DISMISS_KEY = 'ob:gwp:dismissed';
  const DISMISSED_CLASS = 'ob-gwp-dismissed';

  /** Storage throws rather than returns null in a locked-down browser. */
  const isDismissed = () => {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const applyDismissed = () => {
    document.documentElement.classList.add(DISMISSED_CLASS);
  };

  if (isDismissed()) applyDismissed();

  // Delegated, because every panel this fires from is markup the cart replaces.
  // A listener bound to the button would be lost on the first re-render.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-gwp-dismiss]')) return;

    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (error) {
      // Refused storage only costs the shopper the memory of the choice, so
      // hide the panel anyway rather than leaving the button looking broken.
    }

    applyDismissed();
  });

  document.addEventListener('alpine:init', () => {
    // The block renders on the cart page and in the drawer, so this file can be
    // requested twice on one page. Registering twice would warn and clobber.
    if (window.__obGwpRegistered) return;
    window.__obGwpRegistered = true;

    Alpine.data('xGwpGift', (config = {}) => ({
      selected: null,
      selectedSize: null,
      recommended: null,
      recommendedLabel: '',
      changing: false,
      loading: false,
      error: '',
      /** This block sent the shopper to the picker and owes them a way back. */
      awaitingFit: false,

      get canAdd() {
        return Boolean(this.selected) && Boolean(config.hasPurchase) && !this.loading;
      },

      get hasPurchase() {
        return Boolean(config.hasPurchase);
      },

      init() {
        this.readFit();

        // The fit finder is bundled separately and may load after this block.
        // A fresh answer from the picker wins; one that was already stored only
        // fills a choice the shopper has not made yet.
        this._onFitChange = () => this.readFit(true);
        this._onFitReady = () => this.readFit(false);
        this._onFitLock = (event) => this.lockIn(event.detail || {});
        this._onFitClose = () => this.returnToCart();
        document.addEventListener('onebone:fit:change', this._onFitChange);
        document.addEventListener('onebone:fit:ready', this._onFitReady);
        document.addEventListener('onebone:fit:lock', this._onFitLock);
        document.addEventListener('onebone:fit:close', this._onFitClose);

        this.enforceSingleUnit();
      },

      destroy() {
        document.removeEventListener('onebone:fit:change', this._onFitChange);
        document.removeEventListener('onebone:fit:ready', this._onFitReady);
        document.removeEventListener('onebone:fit:lock', this._onFitLock);
        document.removeEventListener('onebone:fit:close', this._onFitClose);
      },

      /** Take the fit finder's bottoms size, if the shopper has ever set one. */
      readFit(overwriteChoice = false) {
        const profile = window.OneBoneFit?.getProfile?.();
        this.useSize(profile?.captured ? profile.bottom : null, overwriteChoice);
      },

      /** Mark a bottoms size as the recommendation, or clear it with nothing. */
      useSize(size, overwriteChoice = false) {
        if (!size) {
          this.recommended = null;
          this.recommendedLabel = '';
          return;
        }

        this.recommended = String(size);
        this.recommendedLabel = `Your fit finder size is ${this.recommended}.`;
        this.applyRecommended(overwriteChoice);
      },

      /**
       * A stored fit is an answer already given, so the size arrives chosen and
       * the gift is one click away. Still marked with the dot, so it is clear
       * where the choice came from and that it can be changed.
       */
      applyRecommended(overwriteChoice = false) {
        if (!this.recommended || (this.selected && !overwriteChoice)) return;

        const match = Array.from(this.$el.querySelectorAll('[data-variant-id]')).find(
          (button) => button.dataset.size === this.recommended && !button.disabled
        );
        if (!match) return;

        this.selected = Number(match.dataset.variantId);
        this.selectedSize = match.dataset.size;
      },

      /**
       * The picker is the header's, opened rather than duplicated. The drawer
       * has to close first — it traps focus, and the picker is a page-level
       * dialog — which is what the header's own trigger does in fit-finder.js.
       */
      openFitFinder() {
        // Claim the trip. The cart page carries this block twice — once in the
        // page, once in the drawer that is also in the DOM — and both hear the
        // picker's events. Only the one that sent the shopper there brings them
        // back, or the drawer would slide open over the cart page.
        this.awaitingFit = true;
        Alpine.store('xMiniCart')?.hideCart?.();

        const openWithIntent = () => window.OneBoneFit?.open?.({ intent: 'gift' });

        window.setTimeout(() => {
          if (window.OneBoneFit?.open) {
            openWithIntent();
            return;
          }

          // fit-finder.js is loaded from the layout, so this is belt and
          // braces: wait for it, and only give up on the size guide page.
          document.addEventListener('onebone:fit:ready', openWithIntent, { once: true });
          window.setTimeout(() => {
            if (!window.OneBoneFit?.open) {
              this.awaitingFit = false;
              window.location.href = '/pages/size-guide';
            }
          }, 1000);
        }, 200);
      },

      /**
       * "Lock in My Size" in the picker: take the answer, put it on a size
       * button, and hand the shopper back to their cart with the free pair one
       * click away.
       *
       * The picker fires this before it closes, so the close that follows finds
       * the trip already claimed and does not return them a second time.
       */
      lockIn(detail = {}) {
        if (!this.awaitingFit) return;
        this.awaitingFit = false;
        this.error = '';

        // The picker persisted the fit before firing, so this reads the answer
        // the shopper just gave and overrides any earlier pick. The size rides
        // along on the event as well, so a browser that refuses local storage
        // still lands on the size the shopper was just shown.
        this.readFit(true);
        if (!this.recommended) this.useSize(detail.bottom, true);

        // applyRecommended leaves the choice alone when the recommended size is
        // not one of the buttons, which on a sold-out size would send the
        // shopper back to a cart that quietly ignored them.
        if (this.recommended && this.selectedSize !== this.recommended) {
          this.error = `Size ${this.recommended} is out of stock right now — pick another below.`;
        }

        this.showCart();
      },

      /**
       * The picker closed without an answer — the X, or the overlay. The drawer
       * was only closed to make room for it, so put it back rather than leaving
       * the shopper on a page with no cart in sight.
       */
      returnToCart() {
        if (!this.awaitingFit) return;
        this.awaitingFit = false;
        this.showCart();
      },

      /**
       * Back to wherever the offer was. In the drawer that means reopening it;
       * on the cart page the cart never went anywhere, so bring the offer into
       * view instead of opening a drawer over the page it is already on.
       */
      showCart() {
        if (config.context === 'drawer') {
          Alpine.store('xMiniCart')?.openCart?.();
          return;
        }

        this.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },

      select(variantId, size) {
        this.error = '';
        this.selected = this.selected === variantId ? null : variantId;
        this.selectedSize = size;
      },

      async add() {
        if (!this.canAdd) return;
        if (window.OneBoneOfferTheme?.getState().assignedArm === 'percent') return;

        // Swapping size on an existing gift line: drop the old one first, so the
        // cart never holds two gift lines for the function to choose between.
        if (config.lineKey) {
          const removed = await this.mutate('cart/change.js', {
            id: config.lineKey,
            quantity: 0,
          });
          if (!removed) return;
        }

        await this.mutate('cart/add.js', {
          items: [
            {
              id: this.selected,
              quantity: 1,
              properties: { [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE },
            },
          ],
        });
      },

      async remove() {
        if (!config.lineKey || this.loading) return;

        await this.mutate('cart/change.js', { id: config.lineKey, quantity: 0 });
      },

      /**
       * The gift is one unit. The cart row still carries the theme's quantity
       * stepper, and the function only ever discounts one, so a shopper who
       * nudges it to two would quietly pay for the second.
       */
      async enforceSingleUnit() {
        if (!config.lineKey || Number(config.lineQuantity) <= 1) return;

        await this.mutate('cart/change.js', { id: config.lineKey, quantity: 1 });
      },

      /**
       * One cart write, done the way the rest of the theme does it: wait for any
       * in-flight update, flag our own, re-render the cart sections, then let the
       * theme know the cart moved.
       *
       * @returns {Promise<boolean>} whether the cart actually changed
       */
      async mutate(route, payload) {
        this.loading = true;
        this.error = '';

        const helper = Alpine.store('xCartHelper');
        await helper.waitForCartUpdate();
        window.updatingCart = true;

        try {
          const response = await fetch(window.Shopify.routes.root + route, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              ...payload,
              sections: helper.getSectionsToRender().map((section) => section.id),
              // Without sections_url Shopify renders the requested sections
              // against the wrong URL context and hands back markup the theme
              // cannot use — the cart changes but the drawer never updates, so
              // the click looks like it did nothing. Every other cart call in
              // this theme passes it.
              sections_url: window.location.pathname,
            }),
          });

          const body = await response.json();

          // The cart API answers 422 with a description rather than throwing.
          if (!response.ok || body.status === 422 || body.status === '422') {
            this.error = body.description || body.message || 'That size just went out of stock.';
            return false;
          }

          await helper.reRenderSections(body.sections);

          const bubble = document.querySelector('#cart-icon-bubble span');
          if (bubble) helper.currentItemCount = parseInt(bubble.innerHTML, 10);

          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
          return true;
        } catch (error) {
          console.error('[gwp] cart update failed', error);
          this.error = 'Something went wrong. Please try again.';
          return false;
        } finally {
          window.updatingCart = false;
          this.loading = false;
        }
      },
    }));
  });
})();
